import { byId, isAr, formatCurrency, formatNumber, attachCopyButton, setupLiveCalculation, detectUserCountry } from './calc-common.js';
import Big from 'big.js';
import { getInvoiceTafgeet } from './invoice-currencies.js';

export const SALARY_COUNTRY_SCHEMES = {
  saudi_citizen: { currency: 'SAR', rate: '0.0975', cap: 45000, labelAr: 'التأمينات الاجتماعية وساند (9.75% على الأساسي والسكن - سقف 45,000 ر.س)', labelEn: 'Saudi GOSI & Saned (9.75% on Basic + Housing, capped at 45k SAR)' },
  saudi_expat: { currency: 'SAR', rate: '0', cap: Infinity, labelAr: 'المقيم في السعودية (0% خصم تأمينات على الموظف - يتحمل صاحب العمل 2% مخاطر مهنية)', labelEn: 'Non-Saudi Expat (0% employee deduction; employer covers 2% hazards)' },
  uae_citizen: { currency: 'AED', rate: '0.05', cap: Infinity, labelAr: 'صندوق المعاشات والتأمينات الاجتماعية الإماراتي GPSSA (5% على الأساسي والسكن)', labelEn: 'UAE GPSSA Pension (5% employee contribution on basic & housing)' },
  kuwait_citizen: { currency: 'KWD', rate: '0.105', cap: 2750, labelAr: 'المؤسسة العامة للتأمينات الاجتماعية الكويتية PIFSS (10.5% حصة الموظف الكويتي)', labelEn: 'Kuwait PIFSS Social Security (10.5% employee contribution)' },
  qatar_citizen: { currency: 'QAR', rate: '0.07', cap: Infinity, labelAr: 'الهيئة العامة للتقاعد والتأمينات الاجتماعية القطرية GRIA (7% حصة الموظف القطري)', labelEn: 'Qatar GRIA Pension (7% employee contribution)' },
  bahrain_citizen: { currency: 'BHD', rate: '0.08', cap: 4000, labelAr: 'الهيئة العامة للتأمين الاجتماعي البحرينية SIO (8% تقاعد وتأمين ضد التعطل)', labelEn: 'Bahrain SIO Social Insurance (8% employee pension & unemployment)' },
  oman_citizen: { currency: 'OMR', rate: '0.075', cap: 3000, labelAr: 'صندوق الحماية الاجتماعية العماني SPF (7.5% حصة الموظف العماني)', labelEn: 'Oman Social Protection Fund SPF (7.5% employee contribution)' },
  egypt_standard: { currency: 'EGP', rate: '0.11', cap: 12600, labelAr: 'التأمينات الاجتماعية المصرية (11% حصة الموظف وفق قانون 148 لسنة 2019)', labelEn: 'Egyptian Social Insurance (11% employee contribution under Law 148/2019)' },
  jordan_standard: { currency: 'JOD', rate: '0.075', cap: 3349, labelAr: 'المؤسسة العامة للضمان الاجتماعي الأردني SSC (7.5% حصة العامل المؤمن عليه)', labelEn: 'Jordan SSC Social Security (7.5% employee share)' },
  morocco_standard: { currency: 'MAD', rate: '0.0674', cap: 6000, labelAr: 'الصندوق الوطني للضمان الاجتماعي والتأمين الصحي بالمغرب CNSS + AMO (6.74%)', labelEn: 'Morocco CNSS & AMO Social Security (6.74% employee contribution)' },
  tunisia_standard: { currency: 'TND', rate: '0.0918', cap: Infinity, labelAr: 'الصندوق الوطني للضمان الاجتماعي التونسي CNSS (9.18% حصة الأجير)', labelEn: 'Tunisia CNSS Social Security (9.18% employee share)' },
  algeria_standard: { currency: 'DZD', rate: '0.09', cap: Infinity, labelAr: 'الصندوق الوطني للتأمينات الاجتماعية للعمال الأجراء بالجزائر CNAS (9%)', labelEn: 'Algeria CNAS Social Security (9% employee contribution)' },
  iraq_standard: { currency: 'IQD', rate: '0.05', cap: Infinity, labelAr: 'التقاعد والضمان الاجتماعي للعمال في العراق (5% حصة العامل)', labelEn: 'Iraq Workers Pension & Social Security (5% employee contribution)' },
  custom: { currency: 'SAR', rate: 'custom', cap: Infinity, labelAr: 'استقطاع مخصص', labelEn: 'Custom Deduction' }
};

export function calculateNetSalary({
  basic = 0,
  housing = 0,
  transport = 0,
  otherAllowances = 0,
  country = 'saudi_citizen',
  customDeductionPercent = 0,
  incomeTax = 0,
  otherDeductions = 0
}) {
  const b = Big(basic);
  const h = Big(housing);
  const t = Big(transport);
  const o = Big(otherAllowances);

  const grossSalary = b.plus(h).plus(t).plus(o);

  let socialInsurance = Big(0);
  let gosiLabel = '';

  const scheme = SALARY_COUNTRY_SCHEMES[country] || SALARY_COUNTRY_SCHEMES.saudi_citizen;

  if (country === 'custom') {
    const pct = Big(customDeductionPercent).div(100);
    socialInsurance = grossSalary.times(pct);
    gosiLabel = isAr ? `استقطاع تأمينات مخصص (${customDeductionPercent}%)` : `Custom Deduction (${customDeductionPercent}%)`;
  } else if (scheme.rate === '0') {
    socialInsurance = Big(0);
    gosiLabel = isAr ? scheme.labelAr : scheme.labelEn;
  } else {
    const rateBig = Big(scheme.rate);
    const applicableWage = country === 'saudi_citizen' || country === 'uae_citizen' ? b.plus(h) : grossSalary;
    const cappedWage = scheme.cap !== Infinity
      ? (applicableWage.gt(scheme.cap) ? Big(scheme.cap) : applicableWage)
      : applicableWage;
    socialInsurance = cappedWage.times(rateBig);
    gosiLabel = isAr ? scheme.labelAr : scheme.labelEn;
  }

  const tax = Big(incomeTax || 0);
  const totalDeductions = socialInsurance.plus(tax).plus(Big(otherDeductions || 0));
  const netSalary = grossSalary.minus(totalDeductions);
  const safeNetSalary = netSalary.gt(0) ? netSalary : Big(0);

  const annualGross = grossSalary.times(12);
  const annualNet = safeNetSalary.times(12);

  return {
    grossSalary: Number(grossSalary.toFixed(2)),
    socialInsurance: Number(socialInsurance.toFixed(2)),
    incomeTax: Number(tax.toFixed(2)),
    otherDeductions: Number(Big(otherDeductions).toFixed(2)),
    totalDeductions: Number(totalDeductions.toFixed(2)),
    netSalary: Number(safeNetSalary.toFixed(2)),
    annualGross: Number(annualGross.toFixed(2)),
    annualNet: Number(annualNet.toFixed(2)),
    gosiLabel
  };
}

export function initNetSalaryCalculator() {
  const container = byId('net-salary-calculator-app');
  if (!container) return;

  const countrySelect = byId('salary-country');
  const customGroup = byId('salary-custom-deduction-group');
  const currencySelect = byId('salary-currency');

  // Automatic country & currency detection with Saudi Arabia (SA / SAR) fallback
  const countryMap = {
    SA: 'saudi_citizen',
    AE: 'uae_citizen',
    KW: 'kuwait_citizen',
    QA: 'qatar_citizen',
    BH: 'bahrain_citizen',
    OM: 'oman_citizen',
    EG: 'egypt_standard',
    JO: 'jordan_standard',
    MA: 'morocco_standard',
    TN: 'tunisia_standard',
    DZ: 'algeria_standard',
    IQ: 'iraq_standard'
  };
  const detectedCode = detectUserCountry() || 'SA';
  const initialScheme = countryMap[detectedCode] || 'saudi_citizen';

  if (countrySelect) {
    countrySelect.value = initialScheme;
  }
  if (currencySelect) {
    currencySelect.value = SALARY_COUNTRY_SCHEMES[initialScheme]?.currency || 'SAR';
  }

  if (countrySelect) {
    countrySelect.addEventListener('change', (e) => {
      const selected = e.target.value;
      if (customGroup) {
        customGroup.style.display = selected === 'custom' ? 'block' : 'none';
      }
      if (currencySelect && SALARY_COUNTRY_SCHEMES[selected]?.currency) {
        currencySelect.value = SALARY_COUNTRY_SCHEMES[selected].currency;
        currencySelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      runSalaryCalc();
    });
  }

  setupLiveCalculation(
    [
      'salary-basic',
      'salary-housing',
      'salary-transport',
      'salary-other-allowances',
      'salary-country',
      'salary-custom-pct',
      'salary-tax-deduction',
      'salary-other-deductions',
      'salary-currency'
    ],
    runSalaryCalc
  );

  attachCopyButton(byId('salary-copy-btn'), () => {
    const net = byId('salary-net-result')?.textContent || '';
    const gross = byId('salary-gross-val')?.textContent || '';
    const ded = byId('salary-deductions-val')?.textContent || '';
    return isAr
      ? `📋 ملخص صافي الراتب المستلم:\n• صافي الراتب الشهري: ${net}\n• إجمالي الراتب بالبدلات: ${gross}\n• مجموع الاستقطاعات: ${ded}`
      : `📋 Net Take-Home Salary Summary:\n• Net Monthly Salary: ${net}\n• Gross Salary: ${gross}\n• Total Deductions: ${ded}`;
  });

  const printBtn = byId('salary-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }
}

function runSalaryCalc() {
  const basic = parseFloat(byId('salary-basic')?.value) || 0;
  const housing = parseFloat(byId('salary-housing')?.value) || 0;
  const transport = parseFloat(byId('salary-transport')?.value) || 0;
  const otherAllowances = parseFloat(byId('salary-other-allowances')?.value) || 0;
  const country = byId('salary-country')?.value || 'saudi_citizen';
  const customDeductionPercent = parseFloat(byId('salary-custom-pct')?.value) || 0;
  const incomeTax = parseFloat(byId('salary-tax-deduction')?.value) || 0;
  const otherDeductions = parseFloat(byId('salary-other-deductions')?.value) || 0;
  const currency = byId('salary-currency')?.value || 'SAR';

  const res = calculateNetSalary({
    basic,
    housing,
    transport,
    otherAllowances,
    country,
    customDeductionPercent,
    incomeTax,
    otherDeductions
  });

  if (byId('salary-net-result')) byId('salary-net-result').textContent = formatCurrency(res.netSalary, currency);
  if (byId('salary-gross-val')) byId('salary-gross-val').textContent = formatCurrency(res.grossSalary, currency);
  if (byId('salary-gosi-val')) byId('salary-gosi-val').textContent = formatCurrency(res.socialInsurance, currency);
  if (byId('salary-tax-val')) byId('salary-tax-val').textContent = formatCurrency(res.incomeTax, currency);
  if (byId('salary-deductions-val')) byId('salary-deductions-val').textContent = formatCurrency(res.totalDeductions, currency);
  if (byId('salary-annual-net')) byId('salary-annual-net').textContent = formatCurrency(res.annualNet, currency);
  if (byId('salary-gosi-note')) byId('salary-gosi-note').textContent = res.gosiLabel;

  const tafgeetEl = byId('salary-tafgeet-val');
  if (tafgeetEl) {
    tafgeetEl.textContent = getInvoiceTafgeet(res.netSalary.toFixed(2), currency);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNetSalaryCalculator);
  } else {
    initNetSalaryCalculator();
  }
}
